import React, { useEffect, useRef, useState, useCallback } from "react";
import "../pages/player/TextScrollAnimation.css";

/**
 * Componente para mostrar texto con animación de scroll horizontal
 * @param {Object} props - Propiedades del componente
 * @param {string} props.text - Texto a mostrar
 * @param {string} props.className - Clases CSS adicionales
 * @param {string} props.elementType - Tipo de elemento HTML (p, h1, etc.)
 * @param {Object} props.style - Estilos adicionales
 */
const ScrollText = ({
  text,
  className = "",
  elementType = "div",
  style = {},
}) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const animationRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  // Referencias para controlar la animación
  const lastTimestampRef = useRef(0);
  const currentPositionRef = useRef(0);
  const directionRef = useRef(1); // 1 para derecha a izquierda, -1 para izquierda a derecha
  const pauseUntilRef = useRef(0); // Timestamp hasta cuando pausar
  const stateRef = useRef("running"); // Estados: 'running', 'pausing-at-end', 'pausing-at-start'

  // Constantes de configuración
  const SCROLL_SPEED = 35; // Velocidad constante: 35 pixeles por segundo
  const PAUSE_DURATION = 1500; // Pausa de 1.5 segundos al llegar a los extremos

  // Función para evaluar si el texto necesita scroll
  const checkIfNeedsScroll = useCallback(() => {
    if (!containerRef.current || !textRef.current || !text) return false;

    const containerWidth = containerRef.current.offsetWidth;
    const textWidth = textRef.current.offsetWidth;

    return textWidth > containerWidth;
  }, [text]);

  // Función que realiza la animación frame por frame
  const animateScroll = useCallback((timestamp) => {
    if (!textRef.current || !containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const textWidth = textRef.current.offsetWidth;
    const maxScroll = textWidth - containerWidth;

    if (maxScroll <= 0) return;

    // Si es el primer frame, inicializar las referencias
    if (lastTimestampRef.current === 0) {
      lastTimestampRef.current = timestamp;
      animationRef.current = requestAnimationFrame(animateScroll);
      return;
    }

    const deltaTime = timestamp - lastTimestampRef.current;
    lastTimestampRef.current = timestamp;

    // Verificar si estamos en pausa
    if (timestamp < pauseUntilRef.current) {
      // Seguimos en pausa, mantener posición actual
      animationRef.current = requestAnimationFrame(animateScroll);
      return;
    }

    // Si acabamos de salir de una pausa, actualizar el estado
    if (stateRef.current === "pausing-at-end") {
      directionRef.current = -1;
      stateRef.current = "running";
    } else if (stateRef.current === "pausing-at-start") {
      directionRef.current = 1;
      stateRef.current = "running";
    }

    // Calcular el desplazamiento en este frame basado en la velocidad constante
    const pixelsToMove = (deltaTime / 1000) * SCROLL_SPEED;

    // Actualizar posición según dirección
    if (directionRef.current === 1) {
      // Moviendo de izquierda a derecha (texto se mueve hacia la izquierda)
      currentPositionRef.current += pixelsToMove;

      // Verificar si alcanzamos el final
      if (currentPositionRef.current >= maxScroll) {
        // Ajustar exactamente al final para evitar sobrepaso
        currentPositionRef.current = maxScroll;

        // Iniciar pausa en el extremo
        stateRef.current = "pausing-at-end";
        pauseUntilRef.current = timestamp + PAUSE_DURATION;
      }
    } else {
      // Moviendo de derecha a izquierda (texto se mueve hacia la derecha)
      currentPositionRef.current -= pixelsToMove;

      // Verificar si alcanzamos el inicio
      if (currentPositionRef.current <= 0) {
        // Ajustar exactamente al inicio para evitar subpaso
        currentPositionRef.current = 0;

        // Iniciar pausa en el extremo
        stateRef.current = "pausing-at-start";
        pauseUntilRef.current = timestamp + PAUSE_DURATION;
      }
    }

    // Aplicar transformación con el valor exacto
    textRef.current.style.transform = `translateX(${-currentPositionRef.current}px)`;

    // Continuar la animación
    animationRef.current = requestAnimationFrame(animateScroll);
  }, []);

  // Iniciar o detener la animación según sea necesario
  const updateScrollAnimation = useCallback(() => {
    if (!containerRef.current || !textRef.current || !text) return;

    // Comprobar si el texto necesita scroll
    const needsToScroll = checkIfNeedsScroll();
    setNeedsScroll(needsToScroll);

    // Cancelar cualquier animación existente
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Resetear referencias
    lastTimestampRef.current = 0;
    currentPositionRef.current = 0;
    directionRef.current = 1;
    pauseUntilRef.current = 0;
    stateRef.current = "running";

    if (needsToScroll) {
      // Iniciar nueva animación
      textRef.current.style.transform = "translateX(0)"; // Posición inicial
      animationRef.current = requestAnimationFrame(animateScroll);
    } else {
      // No se necesita scroll, asegurar posición inicial
      textRef.current.style.transform = "translateX(0)";
    }
  }, [text, checkIfNeedsScroll, animateScroll]);

  // Efecto para inicializar la animación cuando se monta o cambia el texto
  useEffect(() => {
    // Pequeño retraso para asegurar que los elementos estén renderizados correctamente
    const timer = setTimeout(updateScrollAnimation, 50);

    return () => {
      clearTimeout(timer);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [text, updateScrollAnimation]);

  // Efecto para recalcular cuando se redimensiona la ventana
  useEffect(() => {
    const handleResize = () => {
      updateScrollAnimation();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [updateScrollAnimation]);

  // Efecto para pausar la animación cuando el elemento no está visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      } else if (!document.hidden && needsScroll && !animationRef.current) {
        // Al volver a hacerse visible, reiniciar la animación
        lastTimestampRef.current = 0; // Forzar reinicio limpio
        animationRef.current = requestAnimationFrame(animateScroll);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [animateScroll, needsScroll]);

  // Efecto para pausar/reanudar al hacer hover
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const handleMouseEnter = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };

    const handleMouseLeave = () => {
      if (needsScroll && !animationRef.current) {
        lastTimestampRef.current = 0; // Forzar reinicio limpio
        animationRef.current = requestAnimationFrame(animateScroll);
      }
    };

    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [animateScroll, needsScroll]);

  // Limpiar la animación cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, []);

  // Creamos el elemento dinámicamente basado en elementType
  const Element = elementType;

  return (
    <div
      className={`scroll-text-container ${className}`}
      ref={containerRef}
      style={style}
    >
      <Element
        className={`scroll-text ${needsScroll ? "needs-scroll" : ""}`}
        ref={textRef}
      >
        {text}
      </Element>
    </div>
  );
};

export default ScrollText;
